"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
type AuthContextType = {
  loggedIn: boolean;
  setLoggedIn: (v: boolean) => void;
};
const AuthContext = createContext<AuthContextType | null>(null);

// 포커스가 여러 번 오갈 때 status 프로브가 도배되지 않도록 최소 간격을 둔다.
const AUTH_STATUS_REVALIDATE_INTERVAL_MS = 10_000;
export function AuthProvider(t0) {
  const $ = _c(12);
  const {
    initialLoggedIn,
    children
  } = t0;
  const [localLoggedIn, setLocalLoggedIn] = useState(initialLoggedIn);
  const [authStateConfirmed, setAuthStateConfirmed] = useState(initialLoggedIn);
  const manualStateVersion = useRef(0);
  const lastRevalidatedAt = useRef(Number.NEGATIVE_INFINITY);
  const probeSequence = useRef(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = value => {
      manualStateVersion.current = manualStateVersion.current + 1;
      setLocalLoggedIn(value);
      setAuthStateConfirmed(true);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const setLoggedIn = t1;
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const controller = new AbortController();
      const syncAuthStatus = () => {
        const versionAtRequest = manualStateVersion.current;
        probeSequence.current = probeSequence.current + 1;
        const sequenceAtRequest = probeSequence.current;
        lastRevalidatedAt.current = Date.now();
        fetch("/api/auth/status", {
          cache: "no-store",
          credentials: "include",
          signal: controller.signal
        }).then(_temp).then(t4 => {
          const {
            authenticated
          } = t4;
          if (manualStateVersion.current !== versionAtRequest) {
            return;
          }
          if (sequenceAtRequest !== probeSequence.current) {
            return;
          }
          setLocalLoggedIn(authenticated);
          setAuthStateConfirmed(true);
        }).catch(_temp2);
      };
      const revalidateOnReturn = () => {
        if (document.visibilityState === "hidden") {
          return;
        }
        if (Date.now() - lastRevalidatedAt.current < AUTH_STATUS_REVALIDATE_INTERVAL_MS) {
          return;
        }
        syncAuthStatus();
      };
      syncAuthStatus();
      window.addEventListener("focus", revalidateOnReturn);
      document.addEventListener("visibilitychange", revalidateOnReturn);
      return () => {
        controller.abort();
        window.removeEventListener("focus", revalidateOnReturn);
        document.removeEventListener("visibilitychange", revalidateOnReturn);
      };
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== authStateConfirmed || $[4] !== localLoggedIn) {
    t4 = () => {
      if (localLoggedIn) {
        Cookies.set("loggedIn", "1", {
          sameSite: "lax"
        });
      } else {
        if (authStateConfirmed) {
          Cookies.remove("loggedIn");
          Cookies.remove("userInfo");
        }
      }
    };
    t5 = [authStateConfirmed, localLoggedIn];
    $[3] = authStateConfirmed;
    $[4] = localLoggedIn;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== localLoggedIn) {
    t6 = {
      loggedIn: localLoggedIn,
      setLoggedIn
    };
    $[7] = localLoggedIn;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== children || $[10] !== t6) {
    t7 = <AuthContext.Provider value={t6}>{children}</AuthContext.Provider>;
    $[9] = children;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}
function _temp2(error) {
  if (error instanceof DOMException && error.name === "AbortError") {
    return;
  }
  if (process.env.NODE_ENV === "development") {
    console.error("[AuthProvider] Failed to synchronize auth status:", error);
  }
}
async function _temp(response) {
  if (!response.ok) {
    throw new Error(`Auth status failed (${response.status})`);
  }
  return response.json() as Promise<{
    authenticated: boolean;
  }>;
}
export const useAuthContext = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuthContext must be used within AuthProvider");
  }
  return ctx;
};